// Third Party
import uniq from 'lodash/uniq';

// Redux
import api from '../../redux-api';
import { Action, ApiAction, AsyncResult } from '../../index';

// Actions
import { getManyFundsData, invalidateFundData } from '../funds/fund-store';
import { getManyStocksData, invalidateStockData } from '../stocks/stock-store';
import { startLoaderAction, stopLoaderAction } from '../ui/loaders';

// Types
import { ComplianceAlternativesState } from '../types/compliance-alternatives';

// Utils
import { ShelfStatus } from '../../../utils/shelf-management';
import { updateSavedFunds } from '../funds/saved-funds';
import { HOLDING_TYPES } from '../../../utils/portfolios';
import {
  checkCompliancePermissions,
  sortInstrumentsByIds,
  createComparisonItems,
  processComparisonItems,
  createComplianceComparison,
  addComplianceComparisonToInstruments,
  validateAlternatives,
} from './utils/utils';
import Fund from '@/types/fund';
import FundAlternativeResult from '@/types/fund-alternative-result';
import StockAlternativeResult from '@/types/stock-alternative-result';

const INITIAL_STATE: Fund[] = [];

// Actions
const GET_COMPLIANCE_ALTERNATIVES: ApiAction = {
  type: 'connect/compliance-comparison/get-alternatives',
  endpoint: 'funds/:id/alternatives',
};
const GET_STOCK_COMPLIANCE_ALTERNATIVES: ApiAction = {
  type: 'connect/compliance-comparison/get-stock-alternatives',
  endpoint: 'stocks/:id/alternatives',
};
export const ADD_COMPLIANCE_ALTERNATIVES: Action = {
  type: 'connect/compliance-comparison/add-alternatives',
};
const CLEAR_ALTERNATIVES: Action = {
  type: 'connect/compliance-comparison/clear',
};

// Reducer
export default function reducer(state: ComplianceAlternativesState = INITIAL_STATE, action: Action): ComplianceAlternativesState {
  switch (action.type) {
    case CLEAR_ALTERNATIVES.type: {
      return INITIAL_STATE;
    }
    case ADD_COMPLIANCE_ALTERNATIVES.type: {
      return action.alternatives;
    }
    default: {
      return state;
    }
  }
}

// Action Creators
export const clearComplianceAlternatives = () => CLEAR_ALTERNATIVES;

export const getFundComplianceAlternatives =
  (instrumentId: string): AsyncResult<{ funds: FundAlternativeResult[] }> =>
  () => {
    const params = {
      id: instrumentId,
    };

    return api.get(GET_COMPLIANCE_ALTERNATIVES, params);
  };

export const getStockComplianceAlternatives =
  (instrumentId: string): AsyncResult<{ stocks: StockAlternativeResult[] }> =>
  () => {
    const params = {
      id: instrumentId,
    };

    return api.get(GET_STOCK_COMPLIANCE_ALTERNATIVES, params);
  };

export const addFundComplianceAlternatives =
  (instrumentId: string): AsyncResult<string> =>
  async (dispatch, getState) => {
    dispatch(startLoaderAction(ADD_COMPLIANCE_ALTERNATIVES.type, { instrumentId }));
    const state = getState();
    const {
      auth: { user },
      funds: { savedFunds },
    } = state;

    if (!user) {
      dispatch(stopLoaderAction(ADD_COMPLIANCE_ALTERNATIVES.type));
      return Promise.reject(new Error('User not authenticated'));
    }

    try {
      // Get alternatives and check permissions
      const alternatives = await dispatch(getFundComplianceAlternatives(instrumentId));
      const { userCanViewAlternativesWithoutSaving } = checkCompliancePermissions(user);

      // Validate alternatives exist
      validateAlternatives(alternatives.funds);

      // Get fund data and sort by original order
      const ids = [instrumentId, ...alternatives.funds.map((fund: FundAlternativeResult) => fund._id)];
      const funds = await dispatch(getManyFundsData(ids));
      const sortedFunds = sortInstrumentsByIds(ids, funds, userCanViewAlternativesWithoutSaving);

      // Early return for light version users and when no compliance comparison is needed
      if (userCanViewAlternativesWithoutSaving) {
        dispatch({ ...ADD_COMPLIANCE_ALTERNATIVES, alternatives: sortedFunds });
        dispatch(stopLoaderAction(ADD_COMPLIANCE_ALTERNATIVES.type));
        return '';
      }

      // Create comparison and save
      const {
        appConfig: { appRegion },
      } = user;
      const comparisonItems = createComparisonItems(sortedFunds, { user, appRegion, instrumentType: HOLDING_TYPES.FUND });
      const complianceCompleteComparisonItems = processComparisonItems(comparisonItems);

      // Invalidate cache and update saved funds
      dispatch(invalidateFundData(ids));
      const newSavedFunds = savedFunds.funds.map(fund => {
        if (ids.includes(fund._id)) {
          return {
            ...fund,
            flags: uniq([...(fund.flags || []), ShelfStatus.ON_SHELF_KYP_COMPLETE]),
          };
        }
        return fund;
      });
      dispatch(updateSavedFunds(newSavedFunds));

      // Save compliance comparison
      const savedComplianceComparison = await createComplianceComparison(complianceCompleteComparisonItems, {
        user,
        appRegion,
        instrumentType: HOLDING_TYPES.FUND,
      });

      // Add compliance comparison ID to instruments and dispatch
      const complianceAlternatives = addComplianceComparisonToInstruments(sortedFunds, savedComplianceComparison._id);
      dispatch({ ...ADD_COMPLIANCE_ALTERNATIVES, alternatives: complianceAlternatives });
      dispatch(stopLoaderAction(ADD_COMPLIANCE_ALTERNATIVES.type));

      return savedComplianceComparison._id;
    } catch (error) {
      dispatch(stopLoaderAction(ADD_COMPLIANCE_ALTERNATIVES.type));
      return Promise.reject(error);
    }
  };

export const addStockComplianceAlternatives =
  (instrumentId: string): AsyncResult<string> =>
  async (dispatch, getState) => {
    dispatch(startLoaderAction(ADD_COMPLIANCE_ALTERNATIVES.type, { instrumentId }));
    const state = getState();
    const {
      auth: { user },
    } = state;

    if (!user) {
      dispatch(stopLoaderAction(ADD_COMPLIANCE_ALTERNATIVES.type));
      return Promise.reject(new Error('User not authenticated'));
    }

    try {
      // Get alternatives and check permissions
      const alternatives = await dispatch(getStockComplianceAlternatives(instrumentId));
      const { userCanViewAlternativesWithoutSaving } = checkCompliancePermissions(user);

      // Validate alternatives exist
      validateAlternatives(alternatives.stocks);

      // Get stock data and sort by original order
      const ids = [instrumentId, ...alternatives.stocks.map(stock => stock._id)];
      const stocks = await dispatch(getManyStocksData(ids));
      const sortedStocks = sortInstrumentsByIds(ids, stocks, userCanViewAlternativesWithoutSaving);

      // Early return for light version users and when no compliance comparison is needed
      if (userCanViewAlternativesWithoutSaving) {
        dispatch({ ...ADD_COMPLIANCE_ALTERNATIVES, alternatives: sortedStocks });
        dispatch(stopLoaderAction(ADD_COMPLIANCE_ALTERNATIVES.type));
        return '';
      }

      // Create comparison and save
      const {
        appConfig: { appRegion },
      } = user;
      const comparisonItems = createComparisonItems(sortedStocks, { user, appRegion, instrumentType: HOLDING_TYPES.STOCK });
      const complianceCompleteComparisonItems = processComparisonItems(comparisonItems);

      dispatch(invalidateStockData(ids));

      // Save compliance comparison
      const savedComplianceComparison = await createComplianceComparison(complianceCompleteComparisonItems, {
        user,
        appRegion,
        instrumentType: HOLDING_TYPES.STOCK,
      });

      // Add compliance comparison ID to instruments and dispatch
      const complianceAlternatives = addComplianceComparisonToInstruments(sortedStocks, savedComplianceComparison._id);
      dispatch({ ...ADD_COMPLIANCE_ALTERNATIVES, alternatives: complianceAlternatives });
      dispatch(stopLoaderAction(ADD_COMPLIANCE_ALTERNATIVES.type));

      return savedComplianceComparison._id;
    } catch (error) {
      dispatch(stopLoaderAction(ADD_COMPLIANCE_ALTERNATIVES.type));
      return Promise.reject(error);
    }
  };
